import { useApolloClient } from '@apollo/client';
import {
  FormProvider,
  NavProvider,
  Screen,
  useNextRouterNavigator,
} from '@updater/ui-informant';
import { TrackEventInput } from '@updater/ui-tracker';
import { PageTemplate } from 'components/page-template';
import { useInvitedOnboardingStore } from 'flows/core/store';
import { useIsMoveUPO } from 'hooks/useIsMoveUPO';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTracking } from 'react-tracking';
import { Disclaimer } from '../common/Disclaimer';
import {
  FromAddressSwitch,
  FromOutsideCountryAddress,
  FromUsaAddress,
} from '../common/from-address';
import {
  useInitialValues,
  useInviteToken,
  useOnboardingSubmit,
} from '../common/hooks';
import { getSchema } from '../common/schema';
import { getSchemaUpo } from '../common/schemaUpo';
import { ToggleList } from '../common/ToggleList';
import { FormData, OnboardingProps } from '../common/types';
import { formatValues, routeMaybeWithQuery } from '../common/util';
import { makeEmailAvailability, validatePassword } from '../common/validators';
import { useIsEntityKindUser } from 'hooks/useIsEntityKindUser';
import { useFeature } from '@growthbook/growthbook-react';

export const OnboardingFlow: React.FC<OnboardingProps> = ({ invite }) => {
  const router = useRouter();
  const query = router.asPath.includes('?')
    ? router.asPath.split('?').slice(1).join('?')
    : undefined;
  const inviteToken = useInviteToken();
  const { data: isPropertyManagementUser, loading: entityKindLoading } =
    useIsEntityKindUser('property_management', inviteToken);

  const store = useInvitedOnboardingStore();
  const initialValues = useInitialValues(invite);
  const [step] = useState<'confirm' | 'welcome'>('confirm');

  /* Experiment to remove the `Rent or Own` screen */
  const { value: rentOrOwnVariant } = useFeature('consumer_skip_rent_own');

  /* Experiment to remove the `Home Size` screen */
  const { value: skipHomeSizeVariant } = useFeature('consumer_skip_homesize');

  /* Experiment to remove the `Password` screen */
  const { value: skipPasswordVariant } = useFeature('consumer_skip_password');

  const submission = useOnboardingSubmit();

  /* IMPORTANT - flag for determining is user is upo as behavior and
  screens may differ between UPO and non-UPO . There is also `loading` and
  `error` if needed */
  const { data: { isUPO } = {}, loading: loadingUPO } = useIsMoveUPO(true);

  const handleSubmit = async (values: FormData) => {
    const submissionData = formatValues(values);
    await submission.submitOnboarding(submissionData, {
      optIn: values.optIn,
    });

    // Used for alternative welcome page keep for now
    // if (isUPO || invite?.residentOnboardingEnabled) {
    //   setStep('welcome');
    // }
  };

  /* Composed component (Screens) */
  return (
    <PageTemplate
      loading={
        entityKindLoading ||
        loadingUPO ||
        (submission.successful && step !== 'welcome')
      }
      maxWidth={1440}
      hasHeaderMenu={false}
    >
      <FormProvider
        schema={
          isUPO
            ? getSchemaUpo(!!skipHomeSizeVariant, !!skipPasswordVariant)
            : getSchema(!!skipHomeSizeVariant, !!skipPasswordVariant)
        }
        onSubmit={handleSubmit}
        validators={{
          validatePassword,
          verifyEmailAvailability: makeEmailAvailability(
            useApolloClient(),
            inviteToken
          ),
        }}
        trackEvent={useTracking<TrackEventInput<unknown>>().trackEvent}
        initialValues={initialValues as FormData}
        customComponents={{
          ToggleList,
          FromUsaAddress,
          FromOutsideCountryAddress,
        }}
      >
        <NavProvider
          onBeforeNavigate={({ formValues }) => store.setData(formValues)}
          navigator={useNextRouterNavigator({ useBack: true })}
          basePath="/sign-up"
        >
          {/* These screens will override anyting in schema */}
          <Screen
            name="activateScreen"
            heading="Activate your account by confirming your move details"
            subHeading="What's your name?"
            route={routeMaybeWithQuery('', query)}
            fields={['firstName', 'lastName']}
            hideBackButton
          />
          {isPropertyManagementUser ? null : (
            <Screen
              name="moveDateScreen"
              heading="When do you plan to move?"
              subHeading="We just need a rough idea, so if nothing else provide the date your lease starts."
              route={routeMaybeWithQuery('/move-date', query)}
              fields={['moveDate']}
            />
          )}
          <Screen
            name="currentAddressScreen"
            heading="What is your current address?"
            route={routeMaybeWithQuery('/from-address', query)}
            fields={[
              'movingFromOutsideCountry',
              'fromAddress',
              'fromOutsideCountryAddress',
            ]}
            hideNextButton
            templateProps={{
              footerPanel: <FromAddressSwitch />,
            }}
          />
          {isUPO ? null : (
            <Screen
              name="newAddressReviewScreen"
              heading={
                initialValues.phone && invite
                  ? "Let's review your new address"
                  : 'What address are you moving to?'
              }
              route={routeMaybeWithQuery('/to-address', query)}
              fields={['toAddress']}
              hideNextButton
            />
          )}
          {/* If prefilled it has different text */}
          {initialValues.phone && invite ? (
            <Screen
              name="phoneReviewScreen"
              heading="And is this where we can reach you?"
              route={routeMaybeWithQuery('/phone', query)}
              nextButtonText={skipPasswordVariant ? 'Finish' : undefined}
              fields={['phone', 'optIn']}
            />
          ) : (
            <Screen
              name="phoneEntryScreen"
              heading="Where can we reach you?"
              route={routeMaybeWithQuery('/phone', query)}
              nextButtonText={skipPasswordVariant ? 'Finish' : undefined}
              fields={['phone', 'optIn']}
            />
          )}
          {/*Informant doesn't like wrappers around the `Screen` component. So we use useFeature instead */}
          {rentOrOwnVariant === 'skip' ? null : (
            <Screen
              name="rentOrOwnScreen"
              heading="Will you rent or own your new home?"
              subHeading="This will help us determine what you'll need to get done before your move."
              route={routeMaybeWithQuery('/ownership', query)}
              fields={['ownership']}
            />
          )}
          {skipHomeSizeVariant ? null : (
            <Screen
              name="homeSizeScreen"
              heading="How much stuff will you be moving?"
              subHeading="This will help us make recommendations for things like boxes and movers."
              route={routeMaybeWithQuery('/home-size', query)}
              fields={['homeSize']}
            />
          )}
          {skipPasswordVariant ? null : (
            <Screen
              name="lastStepScreen"
              heading="One last step"
              subHeading="Create a password and we'll get going!"
              route={routeMaybeWithQuery('/last-step', query)}
              fields={['email', 'password']}
              nextButtonText="Finish"
              templateProps={{
                footerPanel: <Disclaimer />,
              }}
            />
          )}
        </NavProvider>
      </FormProvider>
      {/* </PropertyImageLandingHandoff> */}
    </PageTemplate>
  );
};
